import { StatusBar } from "expo-status-bar";
import { Pressable, StyleSheet, ScrollView, Text, TextInput, useColorScheme, Alert } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useState } from "react";
import { View } from "../components/Themed";
import { useQuery, gql, useMutation } from "@apollo/client";
import Config from "../constants/Config";
import { v4 as uuidv4 } from "uuid";

const SUPPLIERS_QUERY = gql`
  query getSuppliers($token: String!) {
    suppliers(token: $token) {
      id
      name
      contactName
      email
      phone
      address
      tin
      notes
      createdAt
      updatedAt
    }
  }
`;

const CREATE_SUPPLIER_MUTATION = gql`
  mutation createSupplier(
    $token: String!
    $id: ID!
    $name: String!
    $contactName: String
    $email: String
    $phone: String
    $address: String
    $tin: String
    $notes: String
  ) {
    createSupplier(
      token: $token
      id: $id
      name: $name
      contactName: $contactName
      email: $email
      phone: $phone
      address: $address
      tin: $tin
      notes: $notes
    ) {
      id
      name
    }
  }
`;

const UPDATE_SUPPLIER_MUTATION = gql`
  mutation updateSupplier(
    $token: String!
    $id: ID!
    $name: String
    $contactName: String
    $email: String
    $phone: String
    $address: String
    $tin: String
    $notes: String
  ) {
    updateSupplier(
      token: $token
      id: $id
      name: $name
      contactName: $contactName
      email: $email
      phone: $phone
      address: $address
      tin: $tin
      notes: $notes
    ) {
      id
      name
    }
  }
`;

const DELETE_SUPPLIER_MUTATION = gql`
  mutation deleteSupplier($token: String!, $id: ID!) {
    deleteSupplier(token: $token, id: $id)
  }
`;

export default function SuppliersScreen() {
  const [token, setToken] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [tin, setTin] = useState("");
  const [notes, setNotes] = useState("");
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const { loading, error, data, refetch } = useQuery(SUPPLIERS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [createSupplier] = useMutation(CREATE_SUPPLIER_MUTATION);
  const [updateSupplier] = useMutation(UPDATE_SUPPLIER_MUTATION);
  const [deleteSupplier] = useMutation(DELETE_SUPPLIER_MUTATION);

  useEffect(() => {
    async function fetchToken() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}
    }
    fetchToken();
  }, []);

  const resetForm = () => {
    setName("");
    setContactName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setTin("");
    setNotes("");
    setEditingSupplier(null);
    setShowForm(false);
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setName(supplier.name || "");
    setContactName(supplier.contactName || "");
    setEmail(supplier.email || "");
    setPhone(supplier.phone || "");
    setAddress(supplier.address || "");
    setTin(supplier.tin || "");
    setNotes(supplier.notes || "");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert("Error", "Supplier name is required");
      return;
    }
    try {
      if (editingSupplier) {
        await updateSupplier({
          variables: { token, id: editingSupplier.id, name, contactName, email, phone, address, tin, notes },
        });
      } else {
        await createSupplier({
          variables: { token, id: uuidv4(), name, contactName, email, phone, address, tin, notes },
        });
      }
      resetForm();
      refetch();
    } catch (err) {
      Alert.alert("Error", err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSupplier({ variables: { token, id } });
      refetch();
    } catch (err) {
      Alert.alert("Error", err.message);
    }
  };

  const textColor = isDark ? "#fff" : "#000";
  const cardBg = isDark ? "#1e1e1e" : "#fff";
  const inputBg = isDark ? "#2a2a2a" : "#f5f5f5";

  return (
    <ScrollView style={{ backgroundColor: isDark ? "#121212" : "#f0f0f0" }}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: textColor }]}>Suppliers</Text>
          <Pressable
            style={[styles.addButton, { backgroundColor: Config.companyColor || "#007AFF" }]}
            onPress={() => { resetForm(); setShowForm(!showForm); }}
          >
            <Text style={styles.addButtonText}>{showForm ? "Cancel" : "+ Add Supplier"}</Text>
          </Pressable>
        </View>

        {showForm && (
          <View style={[styles.formCard, { backgroundColor: cardBg }]}>
            <Text style={[styles.formTitle, { color: textColor }]}>
              {editingSupplier ? "Edit Supplier" : "New Supplier"}
            </Text>
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Name *"
              placeholderTextColor="#999"
              value={name}
              onChangeText={setName}
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Contact Name"
              placeholderTextColor="#999"
              value={contactName}
              onChangeText={setContactName}
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Email"
              placeholderTextColor="#999"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Phone"
              placeholderTextColor="#999"
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Address"
              placeholderTextColor="#999"
              value={address}
              onChangeText={setAddress}
              multiline
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Tax ID (TIN)"
              placeholderTextColor="#999"
              value={tin}
              onChangeText={setTin}
            />
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Notes"
              placeholderTextColor="#999"
              value={notes}
              onChangeText={setNotes}
              multiline
            />
            <Pressable style={[styles.saveButton, { backgroundColor: "#28a745" }]} onPress={handleSave}>
              <Text style={styles.saveButtonText}>{editingSupplier ? "Update" : "Create"}</Text>
            </Pressable>
          </View>
        )}

        {loading && <Text style={{ color: textColor }}>Loading...</Text>}
        {error && <Text style={{ color: "#dc3545" }}>Error: {error.message}</Text>}

        {data?.suppliers?.map((supplier) => (
          <View key={supplier.id} style={[styles.supplierCard, { backgroundColor: cardBg }]}>
            <View style={styles.supplierHeader}>
              <Text style={[styles.supplierName, { color: textColor }]}>{supplier.name}</Text>
              <View style={styles.actions}>
                <Pressable style={styles.editButton} onPress={() => handleEdit(supplier)}>
                  <Text style={styles.editButtonText}>Edit</Text>
                </Pressable>
                <Pressable style={styles.deleteButton} onPress={() => handleDelete(supplier.id)}>
                  <Text style={styles.deleteButtonText}>Delete</Text>
                </Pressable>
              </View>
            </View>
            {supplier.contactName && (
              <Text style={[styles.detail, { color: textColor }]}>Contact: {supplier.contactName}</Text>
            )}
            {supplier.email && (
              <Text style={[styles.detail, { color: textColor }]}>Email: {supplier.email}</Text>
            )}
            {supplier.phone && (
              <Text style={[styles.detail, { color: textColor }]}>Phone: {supplier.phone}</Text>
            )}
            {supplier.address && (
              <Text style={[styles.detail, { color: textColor }]}>Address: {supplier.address}</Text>
            )}
            {supplier.tin && (
              <Text style={[styles.detail, { color: textColor }]}>TIN: {supplier.tin}</Text>
            )}
            {supplier.notes && (
              <Text style={[styles.detailNotes, { color: "#888" }]}>{supplier.notes}</Text>
            )}
          </View>
        ))}

        {data?.suppliers?.length === 0 && (
          <Text style={[styles.emptyText, { color: "#888" }]}>No suppliers yet. Add one to get started.</Text>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  addButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  formCard: {
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 16,
  },
  input: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
  },
  saveButton: {
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 8,
  },
  saveButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
  },
  supplierCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
  },
  supplierHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  supplierName: {
    fontSize: 18,
    fontWeight: "bold",
    flex: 1,
  },
  actions: {
    flexDirection: "row",
    gap: 8,
  },
  editButton: {
    backgroundColor: "#007AFF",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  editButtonText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 13,
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  deleteButtonText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 13,
  },
  detail: {
    fontSize: 14,
    marginBottom: 4,
  },
  detailNotes: {
    fontSize: 13,
    fontStyle: "italic",
    marginTop: 4,
  },
  emptyText: {
    textAlign: "center",
    marginTop: 40,
    fontSize: 16,
  },
});
